import { yupResolver } from '@hookform/resolvers/yup'
import { isEqual } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as yup from 'yup'

import { getBarcodeColumn } from './barcode-status-table'
import { getCheckinColumn } from './check-in-table'
import { CheckinHistoryModal } from './CheckInHistoryModal'
import { getTicketColumn } from './ticket-table'
import { getTopUpColumn } from './top-up-table'
import { getTransferHistoryColumn } from './transfer-hystory-table'
import Loader from '../../../common/components/Loader'
import {
  searchByResponseCheckin,
  searchByResponseTickets,
  searchByResponseTopups,
  searchByResponseTransfer,
} from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { xFestivalName } from '../../../core/constants/baseUrls'
import {
  cancelCheckin,
  cancelDashboardfTicket,
  searchByBarcode,
  searchByEmail,
  searchBySerial,
  syncWristband,
} from '../../../core/services/searchBy'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import {
  Button,
  Card,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  InfoIcon,
  SearchIcon,
  XCircleIcon,
} from 'lucide-react'
import { Header, PageTitle } from '@/components'

interface ISupport {
  search: string
}

export const SupportPage: React.FC = () => {
  const [searchBy, setSearchBy] = useState<
    'serial' | 'barcode' | 'email' | 'topupOrder'
  >('email')
  const [ticketColumn, setTicketColumn] = useState<IColumn[]>([])
  const [checkInColumn, setCheckinColumn] = useState<IColumn[]>([])
  const [barcodeColumn, setBarcodeColumn] = useState<IColumn[]>([])
  const [topupsColumn, setTopupsColumn] = useState<IColumn[]>([])
  const [transferHistoryColumn, setTransferHistoryColumn] = useState<IColumn[]>(
    [],
  )
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const { festivalName } = useParams()
  const { user } = useAppSelector((s) => s.app)

  const permissions = ![
    'teodora.croitor@untold.com',
    'kinder@gmail.com',
    'ana.caltea@untold.com',
    'sasa@gmail.com',
    'barleamihai@gmail.com',
    'cosmin@gmail.com',
  ].includes(user?.email || '')

  useEffect(() => {
    if (festivalName) {
      xFestivalName.value = festivalName
    }
  }, [festivalName])

  const [showCheckinHistoryModal, setShowCheckinHistoryModal] =
    useState<boolean>(false)
  const [ticketCode, setTicketCode] = useState<string>('')

  const [tickets, setTickets] = useState<searchByResponseTickets[]>([])
  const [checkin, setTCheckin] = useState<searchByResponseCheckin[]>([])
  const [transfers, setTransfers] = useState<searchByResponseTransfer[]>([])
  const [topups, setTopups] = useState<searchByResponseTopups[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [showCancelCheckinConfirmation, setShowCancelCheckinConfirmation] =
    useState<boolean>(false)
  const [showCancelTicketConfirmation, setShowCancelTicketConfirmation] =
    useState<boolean>(false)
  const [cancelId, setCancelId] = useState<string>()
  const [cancelTicket, setCancelTicket] = useState<string>()

  const handleUpdateTicket = async (ticketCode: string) => {
    try {
      setLoading(true)
      const resp = await syncWristband(ticketCode)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Sync Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancelTicket = async () => {
    try {
      setLoading(true)
      if (cancelTicket) {
        const resp = await cancelDashboardfTicket(cancelTicket)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Cancel Successful')
          document.getElementById('support-form-trigger-button')?.click()
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGetCheckinHistory = async (ticketCode: string) => {
    setTicketCode(ticketCode)
    setShowCheckinHistoryModal(true)
  }

  useEffect(() => {
    setLoading(true)
    setTicketColumn(
      getTicketColumn(
        searchBy,
        handleUpdateTicket,
        handleGetCheckinHistory,
        setCancelTicket,
        setShowCancelTicketConfirmation,
        permissions,
      ),
    )
    setCheckinColumn(
      getCheckinColumn(
        searchBy,
        setShowCancelCheckinConfirmation,
        setCancelId,
        handleGetCheckinHistory,
      ),
    )
    setBarcodeColumn(getBarcodeColumn(searchBy))
    setTopupsColumn(getTopUpColumn(searchBy))
    setTransferHistoryColumn(getTransferHistoryColumn(searchBy))
    setLoading(false)
  }, [searchBy])

  const handleSubmitSearch = async (values: ISupport) => {
    try {
      setLoading(true)
      switch (searchBy) {
        case 'serial':
          const responseSerial = await searchBySerial(
            values.search,
            festivalName,
          )
          setTickets(responseSerial.data.tickets)
          setTCheckin(responseSerial.data.checkin)
          setTransfers(responseSerial.data.transfers)
          setTopups(responseSerial.data.topups)
          break
          break
        case 'barcode':
          const responseBarcode = await searchByBarcode(
            values.search,
            festivalName,
          )
          setTickets(responseBarcode.data.tickets)
          setTCheckin(responseBarcode.data.checkin)
          setTopups(responseBarcode.data.topups)
          setTransfers(responseBarcode.data.transfers)
          break
        case 'email':
          const responseEmail = await searchByEmail(values.search, festivalName)
          setTickets(responseEmail.data.tickets)
          setTCheckin(responseEmail.data.checkin)
          setTopups(responseEmail.data.topups)
          setTransfers(responseEmail.data.transfers)
          break
        case 'topupOrder':
          break
        default:
          console.log('should not happen')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const schema = yup.object().shape({
    search: yup
      .string()
      .required('Field is Required')
      .when([], {
        is: () => isEqual(searchBy, 'email'),
        then: (schema) => schema.email('Not a Valid Email!'),
      }),
  })

  const {
    handleSubmit,
    formState: { errors, isValid },
    reset,
    register,
    watch,
  } = useForm<ISupport>({
    mode: 'onChange',
    resolver: yupResolver<ISupport>(schema),
  })

  const watchSearch = watch('search')

  const cancelFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        if (cancelId) {
          const resp = await cancelCheckin(cancelId)
          setLoading(true)
          switch (searchBy) {
            case 'serial':
              const responseSerial = await searchBySerial(watchSearch)
              setTickets(responseSerial.data.tickets)
              setTCheckin(responseSerial.data.checkin)
              setTransfers(responseSerial.data.transfers)
              setTopups(responseSerial.data.topups)
              break
            case 'barcode':
              const responseBarcode = await searchByBarcode(watchSearch)
              setTickets(responseBarcode.data.tickets)
              setTCheckin(responseBarcode.data.checkin)
              setTransfers(responseBarcode.data.transfers)
              setTopups(responseBarcode.data.topups)
              break
            case 'email':
              const responseEmail = await searchByEmail(watchSearch)
              setTickets(responseEmail.data.tickets)
              setTCheckin(responseEmail.data.checkin)
              setTransfers(responseEmail.data.transfers)
              setTopups(responseEmail.data.topups)
              break
            case 'topupOrder':
              break
            default:
              console.log('should not happen')
          }
          if (resp.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Cancel Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <>
      <Header pageTitle="Support" />
      {loading && <Loader />}
      <Card className="p-5 backdrop-blur-lg max-h-[calc(100vh-135px)] overflow-y-auto">
        <div className="flex flex-col gap-3">
          <PageTitle title="Support" />
          <div className="flex justify-center w-full">
            <div className="flex flex-col gap-3 mb-5">
              <RadioGroup
                onValueChange={(val) => {
                  setSearchBy(
                    val as 'serial' | 'barcode' | 'email' | 'topupOrder',
                  )
                }}
                defaultValue="email"
                className="flex justify-between gap-4 text-monochrome-1"
              >
                <div className="flex gap-2">
                  <RadioGroupItem
                    id="serial"
                    value="serial"
                    checked={isEqual(searchBy, 'serial')}
                  />
                  <Label htmlFor="serial" className="flex flex-col">
                    <span>Search by</span> <strong>Serial Number</strong>
                  </Label>
                </div>
                <div className="flex gap-2">
                  <RadioGroupItem
                    value="barcode"
                    id="barcode"
                    checked={isEqual(searchBy, 'barcode')}
                  />
                  <Label htmlFor="barcode" className="flex flex-col">
                    <span>Search by</span> <strong>Barcode</strong>
                  </Label>
                </div>
                <div className="flex gap-2">
                  <RadioGroupItem
                    value="topupOrder"
                    disabled
                    id="topupOrder"
                    checked={isEqual(searchBy, 'topupOrder')}
                  />
                  <Label htmlFor="topupOrder" className="flex flex-col">
                    <span>Search by</span> <strong>Top Up Order</strong>
                  </Label>
                </div>
                <div className="flex gap-2">
                  <RadioGroupItem
                    value="email"
                    id="email"
                    checked={isEqual(searchBy, 'email')}
                  />
                  <Label htmlFor="email" className="flex flex-col">
                    <span>Search by</span> <strong>Email</strong>
                  </Label>
                </div>
              </RadioGroup>
              <form
                id="support-form"
                className="w-1/2 mx-auto relative"
                onSubmit={handleSubmit(handleSubmitSearch)}
              >
                <div className="flex gap-2 items-center">
                  <Input
                    isInvalid={!!errors?.search?.message}
                    {...register('search')}
                    type="text"
                    placeholder="Search ..."
                    className="p-3 w-full"
                    prefix={<SearchIcon className="w-4 h-4" />}
                  />
                  <Button
                    type="submit"
                    form="support-form"
                    id="support-form-trigger-button"
                    className="h-[44px]"
                  >
                    <SearchIcon className="w-4 h-4" />
                  </Button>
                </div>
              </form>
              {/* <div className="flex gap-3 justify-center">
              <span>
                Barcode: <strong>60j7h0bw</strong>
              </span>
              <span>
                Serial Number: <strong>343230120276</strong>
              </span>
            </div> */}
            </div>
          </div>
        </div>
        <div className="flex w-full justify-center p-3">
          <div className="flex w-full justify-between gap-4">
            <div className="flex gap-2 items-center">
              <CheckCircleIcon className="w-4 h-4 text-success" />
              <span>Sincronizat cu success</span>
            </div>
            <div className="flex gap-2 items-center">
              <XCircleIcon className="w-4 h-4 text-danger" />
              <span>Nesincronizat</span>
            </div>
            <div className="flex gap-2 items-center">
              <InfoIcon className="w-4 h-4 text-muted" />
              <span>Eroare de la sincronizare (raporteaza situatia)</span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-accent-3">COD101</span>
              <span>
                Marcat ca frauda (contacteaza Oana Onicas sau Dutu - Resolution)
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <h4>Ticket</h4>
          <Table columns={ticketColumn} data={tickets} />
        </div>

        <div className="flex flex-col gap-4">
          <h4>Online Check-in</h4>
          <Table columns={checkInColumn} data={checkin} />
        </div>

        {/* <div className="flex flex-col gap-4">
          <h4>Check-in by Barcode Status</h4>
          <Table
            table-hover
            className="bg-white"
            columns={barcodeColumn}
            data={[]}
          />
        </div> */}
        <div className="flex flex-col gap-4">
          <h4>Transfer History</h4>
          <Table
            columnWidth="25%"
            columns={transferHistoryColumn}
            data={transfers}
          />
        </div>
        <div className="flex flex-col gap-4">
          <h4>Top-ups</h4>
          <Table columns={topupsColumn} data={topups} />
        </div>
        <CheckinHistoryModal
          ticketCode={ticketCode}
          showCheckinHistoryModal={showCheckinHistoryModal}
          setShowCheckinHistoryModal={setShowCheckinHistoryModal}
        />
        <DeleteConfirmationModal
          showDeleteConfirmationModal={showCancelCheckinConfirmation}
          setDeleteConfirmationModal={setShowCancelCheckinConfirmation}
          deleteTitle={'Checkin'}
          deleteFunc={cancelFunc}
          cancelText={true}
        />
        <DeleteConfirmationModal
          showDeleteConfirmationModal={showCancelTicketConfirmation}
          setDeleteConfirmationModal={setShowCancelTicketConfirmation}
          deleteTitle={'Ticket'}
          deleteFunc={handleCancelTicket}
          cancelText={true}
        />
        <MessageBox
          message={message}
          setShowMessageBox={setShowMessageBox}
          showMessageBox={showMessageBox}
          isError={isError}
        />
      </Card>
    </>
  )
}
